import { useEffect, useState } from 'react';
import { useMutation, useLazyQuery, gql } from '@apollo/client';
import { Button, Input, Message, Label } from 'semantic-ui-react';

const SUGGEST = gql`
  query UserNameSuggestion($name: String!) {
    userNameSuggestion(name: $name)
  }
`;

const AVAILABLE = gql`
  query UserNameAvailable($userName: String!) {
    userNameAvailable(userName: $userName)
  }
`;

const GRANT_LOGIN = gql`
  mutation GrantLogin($userId: Int!, $userName: String!, $email: String, $makeTeacher: Boolean) {
    grantLogin(userId: $userId, userName: $userName, email: $email, makeTeacher: $makeTeacher) {
      userName
      password
      inviteLink
      emailStatus
    }
  }
`;

/**
 * Giving somebody a way to sign in.
 *
 * ---------------------------------------------------------------------------------------
 * ONE COMPONENT, TWO PLACES
 * ---------------------------------------------------------------------------------------
 *
 * Used from the profile (an existing person who cannot sign in — the common case, since
 * `users(canLogin: false)` is a long list) and from the new-teacher page (somebody created
 * seconds ago). The two differ only in who navigated there, so they share this rather than
 * growing two forms that drift.
 *
 * ---------------------------------------------------------------------------------------
 * THE RESULT IS THE POINT, NOT THE FORM
 * ---------------------------------------------------------------------------------------
 *
 * For a teacher with no email address the password is shown **once** and is then unrecoverable:
 * only the hash is stored, and there is no reset path for somebody who cannot receive mail. So
 * the result state is deliberately loud and does not auto-dismiss — closing it is the only way
 * past it, and that has to be a decision rather than a stray click.
 *
 * Every rule lives on the server. `userNameAvailable` returns the *reason* rather than a
 * boolean so the hint under the field and the error from the mutation are the same sentence,
 * and `grantLogin` re-checks all of it inside its transaction — `Users.userName` has no unique
 * index, so nothing here is the guarantee.
 */

const DELIVERY_LABELS: Record<string, string> = {
    // "Accepted", never "Sent" — SES accepting a message is not delivering it, and a bounce can
    // arrive minutes later. Same wording as the supporter invite results panel.
    ACCEPTED: 'Accepted for delivery',
    REJECTED: 'Rejected by the mail server',
    NOT_CONFIGURED: 'Email is not configured here',
    FAKED_IN_DEV: 'Faked — development',
    NO_ADDRESS: 'No address on file',
};

export interface GiveLoginResult {
    userName: string;
    password: string | null;
    inviteLink: string | null;
    emailStatus: string;
}

export const GiveLogin = ({
    userId,
    name,
    email,
    makeTeacher = true,
    onDone,
    onCancel,
}: {
    userId: number;
    /** Used to suggest a username. */
    name: string;
    /**
     * The address already on file, if any. Blank means the form offers to collect one.
     *
     * **Deliberately not gated on `canAddEmail`.** That field asks whether `setPersonEmail` may
     * fill a blank, and its rule is "has a username, has no address" — so it is false for
     * exactly the people this component exists for, who have neither. Writing an address there
     * would be minting a credential, which is why it refuses; writing one *here* is safe for
     * the opposite reason: a username is being set in the same transaction, so the address
     * never becomes the login identifier on its own.
     */
    email?: string | null;
    /** Adds the unscoped TEACHER role, which is what puts them in the /classes/create picker. */
    makeTeacher?: boolean;
    onDone: (result: GiveLoginResult) => void;
    onCancel?: () => void;
}) => {
    const [userName, setUserName] = useState('');
    const [touched, setTouched] = useState(false);
    const [address, setAddress] = useState('');
    const [problem, setProblem] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [suggest] = useLazyQuery(SUGGEST, { fetchPolicy: 'network-only' });
    const [checkAvailable] = useLazyQuery(AVAILABLE, { fetchPolicy: 'network-only' });
    const [grantLogin, { loading: granting }] = useMutation(GRANT_LOGIN);

    const storedEmail = (email ?? '').trim();

    // Fill the box once, from the name. Not re-run when the administrator edits it — a field
    // that re-suggests while you are typing in it is unusable.
    useEffect(() => {
        let cancelled = false;

        suggest({ variables: { name } }).then((res) => {
            const value = res.data?.userNameSuggestion ?? '';
            if (!cancelled && value !== '') setUserName(value);
        }).catch(() => { /* leave the box empty; they can type one */ });

        return () => { cancelled = true; };
    }, [name, suggest]);

    // Debounced, because this runs a query per keystroke otherwise and the answer only matters
    // when they stop. The mutation re-checks regardless, so a stale hint is cosmetic.
    useEffect(() => {
        if (!touched || userName.trim() === '') {
            setProblem(null);
            return;
        }

        let cancelled = false;
        const timer = setTimeout(() => {
            checkAvailable({ variables: { userName } }).then((res) => {
                if (cancelled) return;
                const reason = res.data?.userNameAvailable ?? '';
                setProblem(reason === '' ? null : reason);
            }).catch(() => { /* the mutation is the real check */ });
        }, 350);

        return () => { cancelled = true; clearTimeout(timer); };
    }, [userName, touched, checkAvailable]);

    const typedAddress = address.trim();
    const effectiveEmail = storedEmail !== '' ? storedEmail : (typedAddress === '' ? null : typedAddress);

    const submit = async () => {
        setError(null);

        try {
            const res = await grantLogin({
                variables: {
                    userId,
                    userName: userName.trim(),
                    // Null rather than '' — the server branches on an address being present, and
                    // an empty string is not one.
                    email: typedAddress === '' ? null : typedAddress,
                    makeTeacher,
                },
            });

            onDone(res.data.grantLogin as GiveLoginResult);
        } catch (e: any) {
            setError(e?.message ?? 'Could not create that login.');
        }
    };

    return (
        <div>
            <div style={{ marginBottom: '0.9em' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25em' }}>
                    Username
                </label>
                <Input
                    fluid
                    size="small"
                    autoFocus
                    placeholder="e.g. rgupta"
                    value={userName}
                    disabled={granting}
                    error={problem != null}
                    onChange={(_, d) => { setTouched(true); setUserName(d.value); }}
                />
                <div style={{ fontSize: '0.85em', marginTop: '0.3em', color: problem ? '#9f3a38' : '#767676' }}>
                    {problem ?? 'This is what they type to sign in. Suggested from their name; change it if you like.'}
                </div>
            </div>

            {storedEmail !== '' ? (
                <Message size="tiny" info style={{ marginBottom: '0.9em' }}>
                    They'll be emailed at <strong>{storedEmail}</strong> and choose their own
                    password. Nobody else ever sees it.
                </Message>
            ) : (
                <div style={{ marginBottom: '0.9em' }}>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25em' }}>
                        Email address <span style={{ fontWeight: 400, color: '#767676' }}>— optional</span>
                    </label>
                    <Input
                        fluid
                        size="small"
                        type="email"
                        placeholder="name@example.org"
                        value={address}
                        disabled={granting}
                        onChange={(_, d) => setAddress(d.value)}
                    />
                    {/* The whole branch, said once, before they commit to it. The no-email path
                        is strictly worse — a second person knows the password and there is no
                        reset — so it must be a choice rather than a thing that happened. */}
                    <div style={{ fontSize: '0.85em', marginTop: '0.3em', color: '#767676' }}>
                        {typedAddress === ''
                            ? 'Without one, a password is generated and shown to you once — write it '
                              + 'down and give it to them. They will not be able to reset it themselves.'
                            : 'They will be emailed a link and choose their own password.'}
                    </div>
                </div>
            )}

            {error && <Message negative size="tiny" content={error} style={{ marginBottom: '0.8em' }} />}

            <Button
                primary
                size="small"
                loading={granting}
                // Not disabled on `problem`: the hint is advisory and can be stale, and the
                // server's refusal is the honest one. Only an empty box is clearly not ready.
                disabled={granting || userName.trim() === ''}
                onClick={submit}
            >
                {effectiveEmail == null ? 'Create login and show password' : 'Create login and send invitation'}
            </Button>
            {onCancel && (
                <Button size="small" basic disabled={granting} onClick={onCancel}>Cancel</Button>
            )}
        </div>
    );
};

/**
 * What happened, shown once.
 *
 * Separate from the form so the new-teacher page can render it as its own step while the
 * profile card renders it in place.
 */
export const GiveLoginOutcome = ({
    result,
    name,
    onClose,
}: {
    result: GiveLoginResult;
    name: string;
    onClose: () => void;
}) => (
    <div>
        {result.password != null ? (
            <Message warning>
                <Message.Header>Write this down now</Message.Header>
                <p style={{ marginTop: '0.4em' }}>
                    It is not stored anywhere and cannot be shown again. If it is lost,
                    {' '}{name} will need a new one creating.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6em', flexWrap: 'wrap', marginTop: '0.6em' }}>
                    <div>
                        <div style={{ fontSize: '0.8em', color: '#767676' }}>Username</div>
                        {/* Monospace, and selectable: this gets copied by hand onto paper as
                            often as it gets copied to a clipboard. */}
                        <code style={{ fontSize: '1.15em', userSelect: 'all' }}>{result.userName}</code>
                    </div>
                    <div>
                        <div style={{ fontSize: '0.8em', color: '#767676' }}>Password</div>
                        <code style={{ fontSize: '1.15em', userSelect: 'all' }}>{result.password}</code>
                    </div>
                    <Button
                        size="tiny"
                        icon="copy"
                        content="Copy both"
                        onClick={() => navigator.clipboard?.writeText(
                            `Username: ${result.userName}\nPassword: ${result.password}`,
                        )}
                    />
                </div>
            </Message>
        ) : (
            // Two states, because "the account was created" and "the email went" are different
            // facts and only the first is certain. Saying "Invitation sent" over a status of
            // NOT_CONFIGURED or REJECTED would be the UI making a promise the code did not
            // keep — and it is precisely then that somebody needs to reach for the link.
            <Message positive={result.emailStatus === 'ACCEPTED'} warning={result.emailStatus !== 'ACCEPTED'}>
                <Message.Header>
                    {result.emailStatus === 'ACCEPTED'
                        ? 'Invitation sent'
                        : 'Login created — but the email did not go'}
                </Message.Header>
                <p style={{ marginTop: '0.4em' }}>
                    {name} will be asked to choose their own password. They'll sign in with the
                    username <code>{result.userName}</code>, which the email tells them.
                </p>
                <p style={{ margin: '0.5em 0' }}>
                    Email: <Label size="tiny">{DELIVERY_LABELS[result.emailStatus] ?? result.emailStatus}</Label>
                </p>
                {result.emailStatus !== 'ACCEPTED' && (
                    <p style={{ margin: '0.5em 0' }}>
                        Send them the link below yourself — it does the same thing.
                    </p>
                )}
                {/* The fallback that makes a failed send recoverable without a second attempt —
                    the same escape hatch the supporter invite results panel offers. */}
                {result.inviteLink && (
                    <Button
                        size="tiny"
                        icon="copy"
                        content="Copy the link instead"
                        onClick={() => navigator.clipboard?.writeText(result.inviteLink!)}
                    />
                )}
                <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.6em' }}>
                    The invitation expires in 7 days.
                </p>
            </Message>
        )}
        <Button size="small" onClick={onClose}>Done</Button>
    </div>
);
